import { createSelector } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import { DefaultRootState } from 'react-redux';
import {
  booleanPreferenceSelector,
  userNameSelector,
} from '../../auth/selectors/auth.selectors';
import { diagnosticsSelector } from '../../diagnostic/selectors/diagnostic.selectors';
import { diagnosticChangedSelector } from '../../diagnostic/selectors/user-diagnostic.selectors';
import {
  savedWavesSelector,
  waveListSelector,
} from '../../wave/selectors/wave.selectors';
import {
  selectAll,
  selectById,
  selectEntities,
} from '../reducers/delineation.reducer';
import { DelineationId } from '../types/delineation.type';
import {
  getDelineationForUser,
  isUserDelineation,
} from '../utils/delineation.utils';

export const delineationStateSelector = ({ delineation }: DefaultRootState) =>
  delineation;

export const delineationSavedSelector = createSelector(
  delineationStateSelector,
  ({ saved }) => saved,
);

export const delineationsSelector = createSelector(
  delineationStateSelector,
  selectAll,
);

export const delineationEntitiesSelector = createSelector(
  delineationStateSelector,
  selectEntities,
);

export const delineationByIdSelector = (delineationId: DelineationId) =>
  createSelector(delineationStateSelector, (delineationState) =>
    selectById(delineationState, delineationId),
  );

export const wavesLoadedSelector = (delineationId: DelineationId) =>
  createSelector(
    delineationByIdSelector(delineationId),
    (delineation) => delineation != null && delineation.waves != null,
  );

export const selectedDelineationIdSelector = createSelector(
  delineationStateSelector,
  ({ selectedId }) => selectedId,
);

export const selectedDelineationSelector = createSelector(
  delineationsSelector,
  delineationEntitiesSelector,
  selectedDelineationIdSelector,
  (list, entities, selectedId) => {
    const delineationById =
      selectedId != null ? entities[selectedId] : undefined;
    return delineationById ?? (list.length > 0 ? list[0] : undefined);
  },
);

export const userDelineationSelector = createSelector(
  delineationsSelector,
  userNameSelector,
  (delineations, userName) =>
    userName == null
      ? undefined
      : getDelineationForUser(delineations, userName),
);

export const expertMatchesUserSelector = createSelector(
  selectedDelineationSelector,
  userNameSelector,
  (delineation, userName) => isUserDelineation(userName, delineation),
);

export const hideImportSelector = createSelector(
  expertMatchesUserSelector,
  selectedDelineationSelector,
  (nameMatches, delineation) => nameMatches && !delineation?.immutable,
);

// When a user delineation doesn't exist we want to be able to import the delineation to our account
// straight away upon clicking on a wave of another user's delineation,
// hence the userDelineation != null condition
export const waveInteractionDisabledSelector = createSelector(
  expertMatchesUserSelector,
  userDelineationSelector,
  (match, userDelineation) => !match && userDelineation != null,
);

export const delineationChangedSelector = createSelector(
  savedWavesSelector,
  waveListSelector,
  (savedWaves, waves) => !isEqual(savedWaves, waves),
);

export const delineationToSaveSelector = createSelector(
  delineationChangedSelector,
  expertMatchesUserSelector,
  (changed, changeAllowed) => changed && changeAllowed,
);

export const allowedDelineationHasChangedSelector = createSelector(
  delineationToSaveSelector,
  booleanPreferenceSelector('allowSaveDiagnostic'),
  booleanPreferenceSelector('allowSaveDelineation'),
  (delineationChanged, canSaveDiagnostic, canSaveDelineation) =>
    !canSaveDiagnostic && canSaveDelineation ? delineationChanged : false,
);

export const allowedDiagnosticHasChangedSelector = createSelector(
  allowedDelineationHasChangedSelector,
  diagnosticChangedSelector,
  booleanPreferenceSelector('allowSaveDiagnostic'),
  booleanPreferenceSelector('allowSaveDelineation'),
  (
    allowedDelineationHasChanged,
    diagnosticChanged,
    canSaveDiagnostic,
    canSaveDelineation,
  ) =>
    canSaveDiagnostic && !canSaveDelineation
      ? diagnosticChanged
      : allowedDelineationHasChanged,
);

export const diagnosticDelineationChangedSelector = createSelector(
  delineationToSaveSelector,
  diagnosticChangedSelector,
  allowedDiagnosticHasChangedSelector,
  booleanPreferenceSelector('allowSaveDiagnostic'),
  booleanPreferenceSelector('allowSaveDelineation'),
  (
    delineationChanged,
    diagnosticChanged,
    allowedDiagnosticHasChanged,
    canSaveDiagnostic,
    canSaveDelineation,
  ) =>
    canSaveDiagnostic && canSaveDelineation
      ? delineationChanged || diagnosticChanged
      : allowedDiagnosticHasChanged,
);

export const changesMadeSelector = createSelector(
  delineationToSaveSelector,
  diagnosticChangedSelector,
  (delineationToSave, diagnosticChanged) => {
    return delineationToSave || diagnosticChanged;
  },
);

export const allowNextNavigation = createSelector(
  diagnosticsSelector,
  userNameSelector,
  delineationSavedSelector,
  (diagnosticList, username, saved) => {
    const userDiagnostic = diagnosticList.find(
      (diagnostic) => diagnostic.expertName === username,
    );
    return userDiagnostic != null || !!saved;
  },
);

export const hasAutoLabelsAndNoDelineationsSelector = createSelector(
  userDelineationSelector,
  booleanPreferenceSelector('autoDiagnostic'),
  (userDelineation, hasAutoDiagnostic) =>
    hasAutoDiagnostic && userDelineation == null,
);
